/**
 * DonutChart riusabile basato su Recharts PieChart.
 *
 * Mirror del componente mobile `src/components/charts/DonutChart.js`:
 *   - cerchio con buco (innerRatio configurabile)
 *   - label centrale + valore centrale
 *   - tooltip che mostra label + valore + unit
 *
 * Le slice ricevono `color` esplicito dalle costanti di dominio
 * (vedi `features/invoices/constants.ts`).
 */

import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'

export type DonutSlice = {
  label: string
  value: number
  color: string
}

type DonutChartProps = {
  data: DonutSlice[]
  size?: number
  /** 0.0 (pie pieno) – 0.95 (donut sottilissimo). Default 0.58. */
  innerRatio?: number
  centerLabel?: string
  centerValue?: string
  unit?: string
}

export function DonutChart({
  data,
  size = 220,
  innerRatio = 0.58,
  centerLabel,
  centerValue,
  unit = '',
}: DonutChartProps) {
  const outerRadius = size / 2 - 2
  const innerRadius = outerRadius * innerRatio

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="label"
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={1}
            stroke="hsl(var(--card))"
            strokeWidth={2}
          >
            {data.map((slice, i) => (
              <Cell key={i} fill={slice.color} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: 'hsl(var(--card))',
              border: '1px solid hsl(var(--border))',
              borderRadius: 8,
              fontSize: 12,
            }}
            labelStyle={{ color: 'hsl(var(--foreground))', fontWeight: 600 }}
            formatter={(value) => [
              `${Number(value).toFixed(2).replace('.', ',')} ${unit}`.trim(),
              undefined,
            ]}
          />
        </PieChart>
      </ResponsiveContainer>

      {(centerLabel || centerValue) && (
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
          {centerLabel && (
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              {centerLabel}
            </span>
          )}
          {centerValue && (
            <span className="mt-0.5 text-base font-bold text-foreground">
              {centerValue}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
